<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import WootSubmitButton from '../../../../components/buttons/FormSubmitButton.vue';
import Modal from '../../../../components/Modal.vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import AttachmentUpload from 'dashboard/components/widgets/AttachmentUpload.vue';

export default {
  name: 'AddCanned',
  components: {
    WootSubmitButton,
    Modal,
    WootMessageEditor,
    AttachmentUpload,
  },
  props: {
    responseContent: {
      type: String,
      default: '',
    },
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      shortCode: '',
      content: this.responseContent || '',
      attachments: [],
      addCanned: {
        showLoading: false,
        message: '',
      },
      show: true,
    };
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(1),
    },
    content: {
      // Không bắt buộc content nếu có attachments
    },
  },
  computed: {
    hasAttachments() {
      return this.attachments && this.attachments.length > 0;
    },
    isFormValid() {
      // Cho phép form valid nếu có content hoặc attachments
      const hasContent = this.content && this.content.trim().length > 0;
      const hasAttachments = this.hasAttachments;

      return (
        (hasContent || hasAttachments) &&
        !this.v$.shortCode.$invalid &&
        !this.addCanned.showLoading
      );
    },
  },
  methods: {
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.attachments = [];
      this.v$.shortCode.$reset();
      this.v$.content.$reset();
    },
    addCannedResponse() {
      // Show loading on button
      this.addCanned.showLoading = true;

      // Prepare data for API call
      const cannedData = {
        short_code: this.shortCode,
        content: this.content,
      };

      // Add attachments if any
      if (this.hasAttachments) {
        cannedData.attachments = this.attachments;
      }

      // Make API Calls
      this.$store
        .dispatch('createCannedResponse', cannedData)
        .then(() => {
          // Reset Form, Show success message
          this.addCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.ADD.API.SUCCESS_MESSAGE'), {type: 'success'});
          this.resetForm();
          this.onClose();
        })
        .catch(error => {
          this.addCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.ADD.API.ERROR_MESSAGE');
          useAlert(errorMessage, {type: 'error'});
        });
    },
    onAttachmentAdded(files) {
      // Convert FileList to Array and add to attachments
      const newFiles = Array.from(files);
      this.attachments = [...this.attachments, ...newFiles];
    },
    onAttachmentRemoved(index) {
      this.attachments.splice(index, 1);
    },
    onAttachmentError(error) {
      useAlert(error.message || this.$t('CANNED_MGMT.ADD.ATTACHMENT_ERROR'), {type: 'error'});
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header
        :header-title="$t('CANNED_MGMT.ADD.TITLE')"
        :header-content="$t('CANNED_MGMT.ADD.DESC')"
      />
      <form class="flex flex-col w-full" @submit.prevent="addCannedResponse()" @keydown.enter.prevent>
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.SHORT_CODE.PLACEHOLDER')"
              @blur="v$.shortCode.$touch"
            />
            <span v-if="v$.shortCode.$error" class="message">
              {{ $t('CANNED_MGMT.ADD.FORM.SHORT_CODE.ERROR') }}
            </span>
          </label>
        </div>

        <div class="w-full">
          <label :class="{ error: v$.content.$error }">
            {{ $t('CANNED_MGMT.ADD.FORM.CONTENT.LABEL') }}
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1 !min-h-[4rem] !max-h-[10rem] overflow-y-auto"
              :class="{ editor_warning: v$.content.$error }"
              enable-variables
              :enable-canned-responses="false"
              :focus-on-mount="false"
              :placeholder="$t('CANNED_MGMT.ADD.FORM.CONTENT.PLACEHOLDER')"
              @blur="v$.content.$touch"
            />
          </div>
        </div>

        <!-- Attachment Upload Section -->
        <div class="w-full">
          <label>
            {{ $t('CANNED_MGMT.ADD.FORM.ATTACHMENTS.LABEL') }}
          </label>
          <AttachmentUpload
            :attachments="attachments"
            :max-files="5"
            :max-size="40 * 1024 * 1024"
            :allowed-file-types="[
              'image/*',
              'video/*',
              'audio/*',
              'application/pdf',
              'application/msword',
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            ]"
            @attachment-added="onAttachmentAdded"
            @attachment-removed="onAttachmentRemoved"
            @attachment-error="onAttachmentError"
          />
          <!-- <div class="attachment-hint">
            {{
              $t('CANNED_MGMT.ADD.FORM.ATTACHMENTS.HINT', {
                maxFiles: 5,
                maxSize: 40,
              })
            }}
          </div> -->
        </div>
        <div class="flex flex-row justify-center w-full gap-2 px-0 py-2">
          <button class="input-base bg-n-gray-50 !text-n-gray-500 rounded-lg" @click.prevent="onClose">
            {{ $t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT') }}
          </button>
          <WootSubmitButton
            class="!bg-n-bluegray-4 !text-white rounded-lg h-10 input-base"
            :disabled="!isFormValid"
            :button-text="$t('CANNED_MGMT.ADD.FORM.SUBMIT')"
            :loading="addCanned.showLoading"
          />      
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }

  .ProseMirror-hichat-style {
    @apply min-h-[12.5rem];

    p {
      @apply text-base;
    }
  }
}
</style>
